import { expandLabelSpec } from './seatLabels';

/**
 * The things on a deck plan that are not seats.
 *
 * <p>Two shapes, because a real plan has both. A <b>divider</b> runs between two rows or two columns
 * and has no width in seats — giving it a position of its own would mean inventing a label for it
 * and shifting every seat after it. A <b>cell</b> occupies grid positions, which is what a table
 * standing where a row of seats would be actually does.</p>
 *
 * <p>Either can be limited to a span, so a gangway that only crosses the centre block does not draw
 * a line across the whole vessel. Spans join with `+` rather than a comma, because commas already
 * separate entries — `table-row:4@D,E` parsed as two entries, the second of them nonsense.</p>
 */
/**
 * Whether aisles and tables can be drawn on a deck plan.
 *
 * <p><b>Off.</b> The grammar, the storage and the drawing all work, but the feature is not finished:
 * a divider's span and weight are only half wired to what the booking wizard's seat picker draws,
 * so a plan laid out with them here does not reproduce faithfully in front of a customer. Rather
 * than ship a tool that produces a plan the customer sees differently, the controls are withdrawn
 * until the picker side lands.</p>
 *
 * <p><b>Nothing is deleted.</b> Decks that already carry aisles or tables keep them in
 * <c>DeckEntity.DeckFeatures</c> untouched — this flag decides only whether they are offered and
 * drawn. Turning it back on brings back both the tools and every divider already saved.</p>
 *
 * <p>Markers are unaffected: they occupy a cell rather than running between them, and that half is
 * finished.</p>
 */
export const DECK_DIVIDERS_ENABLED = false;

export type DeckFeatureType =
	| 'aisle-row'
	| 'aisle-col'
	| 'table-row'
	| 'table-col'
	| 'table-cell'
	| 'icon-cell';

export interface DeckFeature {
	type: DeckFeatureType;
	/** The row or column label the feature is anchored to. */
	anchor: string;
	/** The labels it covers on the other axis. Empty means the full extent. */
	span: string[];
	/**
	 * How thick a divider is drawn, in pixels. Undefined takes the default for its kind.
	 *
	 * <p>Settable because a plan's tables are not all the same thing: one between two rows can be a
	 * narrow ledge, and another is as deep as the seats beside it. Given the cell size, a divider can
	 * be made to match a table drawn in a cell exactly.</p>
	 */
	size?: number;
	/**
	 * How an `icon-cell` is drawn and named.
	 *
	 * <p>Configurable rather than a type per thing: a deck has toilets, stairs, exits, a galley and
	 * whatever else the vessel happens to carry, and none of them behave differently — they mark a
	 * position and are never sold. An icon, a colour and a name the admin chooses cover all of them
	 * without a code change for each, and the name is what the plan's key reads.</p>
	 */
	icon?: string;
	colour?: string;
	label?: string;
}

const TYPES: DeckFeatureType[] = [
	'aisle-row',
	'aisle-col',
	'table-row',
	'table-col',
	'table-cell',
	'icon-cell',
];

/**
 * Reads the stored `type:anchor[@span]` entries.
 *
 * <p>An entry whose type is not recognised is dropped rather than guessed at — a plan drawn from a
 * feature nobody can name is worse than one missing it.</p>
 */
export const parseDeckFeatures = (raw?: string | null): DeckFeature[] =>
	(raw ?? '')
		.split(',')
		.map(entry => entry.trim())
		.filter(Boolean)
		.flatMap(entry => {
			const colon = entry.indexOf(':');
			if (colon < 0) {
				return [];
			}
			const type = entry.slice(0, colon).trim() as DeckFeatureType;
			if (!TYPES.includes(type)) {
				return [];
			}

			let rest = entry.slice(colon + 1);

			// The marker's appearance last of all, so it is off before anything else is read. Written
			// `#icon|colour|label`, the colour without its leading hash so it cannot be mistaken for
			// the start of another marker.
			let icon: string | undefined;
			let colour: string | undefined;
			let label: string | undefined;
			const hash = rest.indexOf('#');
			if (hash >= 0) {
				const [rawIcon, rawColour, rawLabel] = rest.slice(hash + 1).split('|');
				icon = rawIcon?.trim() || undefined;
				colour = rawColour?.trim() ? `#${rawColour.trim().replace(/^#/, '')}` : undefined;
				label = rawLabel?.trim() || undefined;
				rest = rest.slice(0, hash);
			}

			// Thickness next, so it can be stripped before the span is read.
			let size: number | undefined;
			const tilde = rest.indexOf('~');
			if (tilde >= 0) {
				const parsed = Number(rest.slice(tilde + 1));
				size = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
				rest = rest.slice(0, tilde);
			}

			const at = rest.indexOf('@');
			const anchor = (at < 0 ? rest : rest.slice(0, at)).trim();
			if (!anchor) {
				return [];
			}

			// `+` inside a span, translated to the comma form the label parser reads. Ranges such as
			// D-K work either way.
			const span = at < 0 ? [] : expandLabelSpec(rest.slice(at + 1).replace(/\+/g, ','));
			return [{ type, anchor, span, size, icon, colour, label }];
		});

/** Writes them back, omitting the span where a feature covers the full extent. */
export const serialiseDeckFeatures = (features: DeckFeature[]): string =>
	features
		.map(feature => {
			const span = feature.span.length > 0 ? `@${feature.span.join('+')}` : '';
			const size = feature.size ? `~${feature.size}` : '';
			// Trailing separators are trimmed, so an icon with no colour or name stays `#wc`.
			const marker = feature.icon
				? `#${[feature.icon, (feature.colour ?? '').replace(/^#/, ''), feature.label ?? '']
						.join('|')
						.replace(/\|+$/, '')}`
				: '';
			return `${feature.type}:${feature.anchor}${span}${size}${marker}`;
		})
		.join(',');

/** Whether a feature is drawn at this point on its other axis. An empty span covers everything. */
export const featureCovers = (feature: DeckFeature, label: string): boolean =>
	feature.span.length === 0 || feature.span.includes(label);

/** The divider drawn after `anchor` on one axis, or null when none runs there. */
export const dividerAt = (
	features: DeckFeature[],
	anchor: string,
	axis: 'row' | 'col',
): DeckFeature | null =>
	features.find(feature => feature.type === `table-${axis}` && feature.anchor === anchor)
	?? features.find(feature => feature.type === `aisle-${axis}` && feature.anchor === anchor)
	?? null;

/** Every row a table stands in, so the grid can show a row the seats alone would not produce. */
export const tableRows = (features: DeckFeature[]): string[] => [
	...new Set(features.filter(feature => feature.type === 'table-cell').map(feature => feature.anchor)),
];

/** Whether a table occupies this exact cell. */
export const isTableCell = (features: DeckFeature[], rowLabel: string, seatNumber: string): boolean =>
	features.some(
		feature =>
			feature.type === 'table-cell'
			&& feature.anchor === rowLabel
			&& featureCovers(feature, seatNumber),
	);

/** Adds or removes one cell from the table covering that row, keeping the entry per row. */
export const toggleTableCell = (
	features: DeckFeature[],
	rowLabel: string,
	seatNumber: string,
): DeckFeature[] => {
	const existing = features.find(
		feature => feature.type === 'table-cell' && feature.anchor === rowLabel,
	);

	if (!existing) {
		return [...features, { type: 'table-cell', anchor: rowLabel, span: [seatNumber] }];
	}

	const span = existing.span.includes(seatNumber)
		? existing.span.filter(x => x !== seatNumber)
		: [...existing.span, seatNumber];

	// A table covering nothing is not a table — drop the entry rather than leave an empty span,
	// which would otherwise read as "the whole row".
	return span.length === 0
		? features.filter(feature => feature !== existing)
		: features.map(feature => (feature === existing ? { ...feature, span } : feature));
};

/**
 * Adds, resizes or removes a divider after `anchor`.
 *
 * <p>Clicking one that is already the chosen thickness removes it; clicking one of a different
 * thickness sets it instead, so changing a divider's size does not mean deleting and redrawing
 * it.</p>
 */
export const toggleDivider = (
	features: DeckFeature[],
	type: Exclude<DeckFeatureType, 'table-cell'>,
	anchor: string,
	size?: number,
): DeckFeature[] => {
	const existing = features.find(feature => feature.type === type && feature.anchor === anchor);

	if (!existing) {
		return [...features, { type, anchor, span: [], size }];
	}
	if (existing.size === size) {
		return features.filter(feature => feature !== existing);
	}
	return features.map(feature => (feature === existing ? { ...feature, size } : feature));
};

/**
 * Adds or removes one label from a divider's span, so a gangway or table can cross part of the deck
 * rather than all of it.
 *
 * <p>`allLabels` is the full extent of the other axis. It is needed because a stored entry with no
 * span means "the whole way across": to take one column out of a full-width divider the span has to
 * be written out first, and only this caller knows what the whole way across currently is.</p>
 *
 * <p>A span that grows back to cover everything is stored as full again, and one that empties
 * removes the divider — an empty span would otherwise read as full and turn a deleted divider into
 * a deck-wide one.</p>
 */
export const toggleDividerSpan = (
	features: DeckFeature[],
	type: Exclude<DeckFeatureType, 'table-cell'>,
	anchor: string,
	label: string,
	allLabels: string[],
	size?: number,
): DeckFeature[] => {
	const existing = features.find(feature => feature.type === type && feature.anchor === anchor);

	if (!existing) {
		return [...features, { type, anchor, span: [label], size }];
	}

	const current = existing.span.length === 0 ? allLabels : existing.span;
	const next = current.includes(label)
		? current.filter(x => x !== label)
		: [...current, label];

	if (next.length === 0) {
		return features.filter(feature => feature !== existing);
	}

	const coversEverything = allLabels.length > 0 && allLabels.every(x => next.includes(x));
	return features.map(feature =>
		feature === existing ? { ...feature, span: coversEverything ? [] : next, size } : feature,
	);
};

/**
 * Everything the Remove tool clears at one cell, in the order a person would expect: the seat first,
 * then a table standing in that cell, and only when neither is there the dividers anchored on that
 * row or column.
 *
 * <p>Ordered rather than all-at-once so one click never removes two things the admin can see.</p>
 */
export const removeFeaturesAt = (
	features: DeckFeature[],
	rowLabel: string,
	seatNumber: string,
	hadSeat: boolean,
): DeckFeature[] => {
	if (hadSeat) {
		return features;
	}

	if (isTableCell(features, rowLabel, seatNumber)) {
		return toggleTableCell(features, rowLabel, seatNumber);
	}

	return features.filter(
		feature =>
			!(
				(feature.type === 'aisle-row' || feature.type === 'table-row')
				&& feature.anchor === rowLabel
			)
			&& !(
				(feature.type === 'aisle-col' || feature.type === 'table-col')
				&& feature.anchor === seatNumber
			),
	);
};

/** The marker on this cell, or undefined where there is none. */
export const markerAt = (
	features: DeckFeature[],
	rowLabel: string,
	seatNumber: string,
): DeckFeature | undefined =>
	features.find(
		feature =>
			feature.type === 'icon-cell'
			&& feature.anchor === rowLabel
			&& featureCovers(feature, seatNumber),
	);

/**
 * One entry per distinct marker, for the plan's key.
 *
 * <p>Keyed on all three of icon, colour and name, because two markers that differ in any of them
 * read as different things to whoever is looking at the plan.</p>
 */
export const markerLegend = (
	features: DeckFeature[],
): { icon: string; colour?: string; label?: string }[] => {
	const seen = new Map<string, { icon: string; colour?: string; label?: string }>();
	features
		.filter(feature => feature.type === 'icon-cell' && feature.icon)
		.forEach(feature => {
			const key = `${feature.icon}|${feature.colour ?? ''}|${feature.label ?? ''}`;
			if (!seen.has(key)) {
				seen.set(key, { icon: feature.icon!, colour: feature.colour, label: feature.label });
			}
		});
	return [...seen.values()];
};

/**
 * Adds or removes one cell from the marker of that icon on that row.
 *
 * <p>Grouped by row <b>and</b> icon, because a row can carry two different markers — a toilet at one
 * end and stairs at the other — and folding them into one entry would put the wrong symbol on half
 * of them.</p>
 */
export const toggleIconCell = (
	features: DeckFeature[],
	rowLabel: string,
	seatNumber: string,
	marker: { icon: string; colour?: string; label?: string },
): DeckFeature[] => {
	// Any marker already on this cell comes off first, whatever its icon: a cell holds one marker,
	// and clicking it with a different icon selected means "make it that" rather than "stack them".
	const occupant = features.find(
		feature =>
			feature.type === 'icon-cell'
			&& feature.anchor === rowLabel
			&& featureCovers(feature, seatNumber),
	);

	if (occupant) {
		const span = occupant.span.filter(x => x !== seatNumber);
		const without =
			span.length === 0
				? features.filter(feature => feature !== occupant)
				: features.map(feature => (feature === occupant ? { ...feature, span } : feature));

		// Clicking with the same marker clears it; with a different one, replaces it.
		return occupant.icon === marker.icon
			&& occupant.colour === marker.colour
			&& occupant.label === marker.label
			? without
			: toggleIconCell(without, rowLabel, seatNumber, marker);
	}

	const existing = features.find(
		feature =>
			feature.type === 'icon-cell'
			&& feature.anchor === rowLabel
			&& feature.icon === marker.icon
			&& feature.colour === marker.colour
			&& feature.label === marker.label,
	);

	return existing
		? features.map(feature =>
				feature === existing ? { ...feature, span: [...feature.span, seatNumber] } : feature,
			)
		: [...features, { type: 'icon-cell', anchor: rowLabel, span: [seatNumber], ...marker }];
};

/** Every row a marker stands in, so a row that holds only markers still appears on the plan. */
export const iconRows = (features: DeckFeature[]): string[] => [
	...new Set(features.filter(feature => feature.type === 'icon-cell').map(feature => feature.anchor)),
];
